/**
 * Shared building blocks for the message schemas: ids, zone references, card references, enums.
 *
 * Every schema mirrors a type exported by `@play/engine`; `typecheck.ts` proves at compile time that
 * each `z.infer` equals its engine type, so the wire format can't drift from the engine.
 */
import {
  type JsonObject,
  BATTLEFIELD_ID_PATTERN,
  CHAIN_ITEM_ID_PATTERN,
  DECISION_ID_PATTERN,
  DECISION_KINDS,
  DOMAINS,
  PHASES,
  PLAYER_ID_PATTERN,
  PLAYER_ZONE_KINDS,
} from '@play/engine';
import { z } from 'zod';

/** Max length of free identifiers (card ids, printing ids, match ids) on the wire. */
export const MAX_ID_LENGTH = 128;

export const PlayerIdSchema = z
  .templateLiteral(['p', z.int()])
  .refine((s) => PLAYER_ID_PATTERN.test(s), 'Invalid player id');
export const ObjectIdSchema = z.templateLiteral(['o', z.string().regex(/^[0-9a-z]{6}$/)]);
export const BattlefieldIdSchema = z
  .templateLiteral(['bf', z.int()])
  .refine((s) => BATTLEFIELD_ID_PATTERN.test(s), 'Invalid battlefield id');
export const ChainItemIdSchema = z
  .templateLiteral(['c', z.int()])
  .refine((s) => CHAIN_ITEM_ID_PATTERN.test(s), 'Invalid chain item id');
export const DecisionIdSchema = z
  .templateLiteral(['d', z.int()])
  .refine((s) => DECISION_ID_PATTERN.test(s), 'Invalid decision id');
export const TokenIdSchema = z.templateLiteral(['token:', z.string().regex(/^[a-z][a-z0-9-]{0,40}$/)]);
export const CardIdSchema = z.string().min(1).max(MAX_ID_LENGTH);
export const PrintingIdSchema = z.string().min(1).max(MAX_ID_LENGTH);

export const TierSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export const PhaseSchema = z.enum(PHASES);
export const DomainSchema = z.enum(DOMAINS);
export const DecisionKindSchema = z.enum(DECISION_KINDS);
export const PlayerZoneKindSchema = z.enum(PLAYER_ZONE_KINDS);
export const DeckZoneKindSchema = z.enum(['mainDeck', 'runeDeck']);
export const ObjectCategorySchema = z.enum(['card', 'rune', 'legend', 'battlefield']);

export const ZoneRefSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: PlayerZoneKindSchema, player: PlayerIdSchema }),
  z.strictObject({ kind: z.literal('battlefield'), battlefield: BattlefieldIdSchema }),
  z.strictObject({ kind: z.literal('facedown'), battlefield: BattlefieldIdSchema }),
  z.strictObject({ kind: z.literal('chain') }),
]);

/** `top`, `bottom`, or an index from the top. */
export const DeckPositionSchema = z.union([z.literal('top'), z.literal('bottom'), z.int().min(0).max(200)]);

export const AudienceSchema = z.union([z.literal('all'), z.array(PlayerIdSchema).max(8)]);

export const CardRefSchema = z.strictObject({
  cardId: CardIdSchema,
  printingId: PrintingIdSchema.nullable(),
});

export const ViewerSchema = z.union([PlayerIdSchema, z.literal('spectator')]);

/**
 * A JSON object (validated by `z.json()`), typed as the engine's `JsonObject` so schema and engine
 * types stay identical.
 */
export const JsonObjectSchema = z.record(z.string(), z.json()) as unknown as z.ZodType<JsonObject>;

/** A whole number in `[min, max]`. */
export const int = (min: number, max: number) => z.int().min(min).max(max);

/** A short, non-empty string. */
export const text = (max: number) => z.string().min(1).max(max);
